import { ProductStoreCategory } from "../../api/fetchProductStoreCategories/types";
import { Column } from "@/components/Datatable/types";
import { Button } from "@/components/ui/button";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Edit, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function createCategoryColumns(
  onEdit: (category: ProductStoreCategory) => void,
  onDelete: (category: ProductStoreCategory) => void,
): Column<ProductStoreCategory>[] {
  return [
    {
      header: "Name",
      accessorKey: "name",
      cell: ({ row }) => (
        <span className="text-gray-900 dark:text-white">
          {row.original.name}
        </span>
      ),
      enableSorting: true,
    },
    {
      header: "Color",
      accessorKey: "color.value",
      enableSorting: false,
      cell: ({ row }) => {
        const colorHex = row.original.color?.value;
        if (!colorHex) {
          return "—";
        }
        return (
          <div className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ backgroundColor: colorHex }}
            />
            <span>{colorHex}</span>
          </div>
        );
      },
    },
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row }) => (
        <span
          className="text-gray-500 dark:text-gray-400 truncate block max-w-xs"
          title={row.original.description || ""}
        >
          {row.original.description || "-"}
        </span>
      ),
    },
    {
      header: "Product Count",
      accessorKey: "productCount",
      enableSorting: true,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const hasProducts = row.original.productCount > 0;
        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                className="h-8 w-8 p-0 hover:bg-gray-200 transition-colors"
                variant="ghost"
              >
                <span className="sr-only">Open menu</span>
                <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px] p-2 bg-white">
              <DropdownMenuItem
                className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => onEdit(row.original)}
              >
                <Edit className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={`flex items-center px-3 py-2 text-sm transition-colors ${
                  hasProducts
                    ? "cursor-not-allowed text-gray-400"
                    : "cursor-pointer text-red-600 hover:bg-gray-100"
                }`}
                disabled={hasProducts}
                onClick={() => !hasProducts && onDelete(row.original)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
}
